import { ReactNode } from "react";
import { motion } from "framer-motion";
import { cn } from "../../utils/helpers";
import { Maximize2 } from "lucide-react";

interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
  metric?: string;
  trend?: string;
  trendUp?: boolean;
  variant?: "default" | "glass" | "neon";
  onEnlarge?: () => void; // Added prop for functionality
}

export const ChartCard = ({
  title,
  subtitle,
  children,
  className,
  metric,
  trend,
  trendUp,
  variant = "glass", // Default to glass for premium look
  onEnlarge,
}: ChartCardProps) => {
  const variants = {
    default:
      "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800",
    glass:
      "bg-white/70 dark:bg-slate-900/60 backdrop-blur-2xl border-white/40 dark:border-white/5 shadow-xl",
    neon: "bg-slate-950 border-indigo-500/30 shadow-[0_0_30px_rgba(99,102,241,0.1)]",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={cn(
        "relative min-w-0 rounded-2xl border flex flex-col overflow-hidden group transition-all duration-300 hover:shadow-2xl hover:-translate-y-1",
        variants[variant],
        className,
      )}
    >
      {/* Noise Texture Overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.07] pointer-events-none z-0 mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full p-5">
        <div className="flex justify-between items-start mb-4 pr-8">
          {" "}
          {/* Added pr-8 to avoid title overlap with button */}
          <div>
            <h3 className="font-bold text-slate-800 dark:text-white text-lg tracking-tight">
              {title}
            </h3>
            {subtitle && (
              <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-1 opacity-80">
                {subtitle}
              </p>
            )}
          </div>
          {(metric || trend) && (
            <div className="text-right">
              {metric && (
                <p className="text-xl font-bold text-slate-900 dark:text-white font-mono">
                  {metric}
                </p>
              )}
              {trend && (
                <p
                  className={cn(
                    "text-xs font-bold flex items-center justify-end gap-1",
                    trendUp ? "text-emerald-500" : "text-rose-500",
                  )}
                >
                  {trendUp ? "▲" : "▼"} {trend}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex-1 w-full min-h-0 overflow-hidden">{children}</div>
      </div>

      {/* Hover Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/20 dark:via-indigo-500/5 dark:to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      {/* Functional Enlarge Button */}
      {onEnlarge && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEnlarge();
          }}
          className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity p-2 text-slate-400 hover:text-indigo-600 z-20 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
          title="Enlarge View"
        >
          <Maximize2 size={18} />
        </button>
      )}
    </motion.div>
  );
};
